import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, Spinner, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faPaperPlane } from "@fortawesome/free-solid-svg-icons";

import { API } from "../data/API/API";
import "./NullReportingReviewPage.scss";
import {
  CreateNullReportingReviewRequest,
  NullReportingReviewDetailsResponse,
  NullReportingReviewsAPI,
} from "../data/API/NullReportingReview";
import { NullReportingContactsAPI } from "../data/API/NullReportingContacts";
import RTE from "../components/RTE/RTE";

type StepStatus = "active" | "complete" | "pending";

const stepMetaFor = (t: "email" | "phone") =>
  t === "phone"
    ? [
        { step: 1, label: "Kontaktförsök" },
        { step: 2, label: "Svar mottaget" },
        { step: 3, label: "Slutmarkerad" },
      ]
    : [
        { step: 1, label: "Utskick" },
        { step: 2, label: "Svar väntas" },
        { step: 3, label: "Slutmarkerad" },
      ];

function getStepState(status: number): { s1: StepStatus; s2: StepStatus; s3: StepStatus } {
  if (status === 0) return { s1: "active", s2: "pending", s3: "pending" };
  if (status === 1 || status === 2) return { s1: "complete", s2: "active", s3: "pending" };
  return { s1: "complete", s2: "complete", s3: "active" };
}
const decodeHtmlEntities = (input: string) => {
  if (!input) return "";
  const txt = document.createElement("textarea");
  txt.innerHTML = input;
  return txt.value;
};
const Stepper: React.FC<{ status: number; reviewType: "email" | "phone" }> = ({ status, reviewType }) => {
  const st = getStepState(status);
  const states: StepStatus[] = [st.s1, st.s2, st.s3];
  const stepMeta = stepMetaFor(reviewType);

  return (
    <div className="nr-review-stepper">
      {stepMeta.map((m, idx) => {
        const s = states[idx];
        return (
          <React.Fragment key={m.step}>
            <div className="nr-review-step">
              <div className={`nr-review-step-circle ${s}`}>
                {s === "complete" ? <FontAwesomeIcon icon={faCheck} /> : m.step}
              </div>
              <div className={`nr-review-step-label ${s}`}>{m.label}</div>
            </div>
            {idx < 2 && <div className="nr-review-step-line" />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

type EmailTemplateRow = {
  id: number;
  templateName: string;
  header?: string | null;
  body?: string | null;
  active?: boolean | null;
};

const toDateTimeLocalNow = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const getOutcomeBadge = (outcome?: number | null) => {
  switch (outcome) {
    case 1:
      return <span className="badge bg-secondary">Inget svar</span>;
    case 2:
      return <span className="badge bg-warning text-dark">Upptaget</span>;
    case 3:
      return <span className="badge bg-success">Svar erhållet</span>;
    default:
      return null;
  }
};
const NullReportingReviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [sp] = useSearchParams();
  const navigate = useNavigate();

  const isNew = !id;
  const supplierOrgNr = sp.get("supplierOrgNr") ?? "";
  const period = sp.get("period") ?? "";

  const typeParam = (sp.get("type") ?? "email").toLowerCase();
  const newReviewType: "email" | "phone" = typeParam === "phone" ? "phone" : "email";

  const [loading, setLoading] = React.useState(!isNew);
  const [details, setDetails] = React.useState<NullReportingReviewDetailsResponse | null>(null);

  // templates
  const [templatesLoading, setTemplatesLoading] = React.useState(true);
  const [templates, setTemplates] = React.useState<EmailTemplateRow[]>([]);

  // email form state (new)
  const [receiverMail, setReceiverMail] = React.useState<string>("");
  const [emailTemplateId, setEmailTemplateId] = React.useState<number | null>(null);
  const [subject, setSubject] = React.useState("");
  const [body, setBody] = React.useState("");

  // contacts (email new)
  const [contactsLoading, setContactsLoading] = React.useState(false);
  const [contacts, setContacts] = React.useState<Array<{ id: number; mail: string; displayName?: string | null }>>([]);
  const [selectedContactId, setSelectedContactId] = React.useState<number | "">("");

  // phone new attempt form
  const [phoneContactName, setPhoneContactName] = React.useState("");
  const [phoneAttemptAt, setPhoneAttemptAt] = React.useState(() => toDateTimeLocalNow());
  const [phoneNotes, setPhoneNotes] = React.useState("");
  // phone outcomes
  const [phoneOutcome, setPhoneOutcome] = React.useState<number | "">("");
  const [newAttemptOutcome, setNewAttemptOutcome] = React.useState<number | "">("");
  // followup state
  const [supplierReply, setSupplierReply] = React.useState("");
  const [handledComment, setHandledComment] = React.useState("");

  // followup: add new attempt
  const [newAttemptContactName, setNewAttemptContactName] = React.useState("");
  const [newAttemptAt, setNewAttemptAt] = React.useState("");
  const [newAttemptNotes, setNewAttemptNotes] = React.useState("");

  const status = details?.review?.status ?? 0;
  const reviewType: "email" | "phone" = isNew ? newReviewType : details?.review?.reviewType === 1 ? "phone" : "email";

  // --- load contacts for NEW email only
  React.useEffect(() => {
    if (!isNew) return;
    if (reviewType !== "email") return;
    if (!supplierOrgNr) return;

    const run = async () => {
      setContactsLoading(true);
      try {
        const rows = await NullReportingContactsAPI.listSupplierContacts(supplierOrgNr);
        setContacts(rows);

        // auto-select om exakt 1 kontakt
        if (rows.length === 1) {
          setSelectedContactId(rows[0].id);
          setReceiverMail(rows[0].mail);
        }
      } finally {
        setContactsLoading(false);
      }
    };

    run();
  }, [isNew, supplierOrgNr, reviewType]);

  // ---- Load templates (once) (only relevant for email)
  React.useEffect(() => {
    const run = async () => {
      setTemplatesLoading(true);
      try {
        const list = (await API.EmailTemplates.GetAllEmailTemplates()) as EmailTemplateRow[];
        const active = (list || []).filter((t) => t.active !== false);
        setTemplates(active);

        if (isNew && !emailTemplateId && active.length > 0) {
          setEmailTemplateId(active[0].id);
        }
      } finally {
        setTemplatesLoading(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- When template changes: autopopulate subject/body (only in NEW + email)
  React.useEffect(() => {
    if (!isNew) return;
    if (reviewType !== "email") return;
    if (!emailTemplateId) return;

    const t = templates.find((x) => x.id === emailTemplateId);
    if (!t) return;

    setSubject(t.header ?? "");
    setBody(decodeHtmlEntities(t.body ?? ""));
  }, [emailTemplateId, templates, isNew, reviewType]);

  // ---- Load details when viewing existing review
  const load = React.useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await NullReportingReviewsAPI.getById(Number(id));
      setDetails(res);

      // followup defaults
      setSupplierReply(res.review.supplierReplyText ?? "");
      setHandledComment(res.review.handledComment ?? "");

      // email fields (safe for phone = empty)
      setReceiverMail(res.review.receiverMail ?? "");
      setEmailTemplateId(res.review.emailTemplateId ?? null);
      setSubject(res.review.emailSubject ?? "");
      setBody(decodeHtmlEntities(res.review.emailBody ?? ""));

      // phone followup default attempt time
      if (res.review.reviewType === 1) {
        setNewAttemptAt(toDateTimeLocalNow());
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    if (!isNew) load();
  }, [isNew, load]);

  const sendTestToMe = async () => {
    await API.Emails.sendTestEmail(subject, body);
  };

  const onSelectContact = (val: string) => {
    if (!val) {
      setSelectedContactId("");
      return;
    }
    const cid = Number(val);
    setSelectedContactId(cid);

    const c = contacts.find((x) => x.id === cid);
    if (c?.mail) setReceiverMail(c.mail);
  };

  const createAndSendEmail = async () => {
    if (!supplierOrgNr || !period) throw new Error("supplierOrgNr/period saknas");
    if (!receiverMail) throw new Error("receiverMail saknas");

    const created = await NullReportingReviewsAPI.createDraft({
      supplierOrgNr,
      period,
      reviewType: 0,
      receiverMail,
      emailTemplateId,
      emailSubject: subject,
      emailBody: body,
    } as CreateNullReportingReviewRequest);

    await NullReportingReviewsAPI.send(created.id);
    navigate(`/nullreporting/reviews/${created.id}`);
  };

  const createPhoneReview = async () => {
    if (!supplierOrgNr || !period) throw new Error("supplierOrgNr/period saknas");
    if (!phoneContactName.trim()) throw new Error("Kontaktperson saknas");
    if (!phoneAttemptAt) throw new Error("Datum/tid saknas");

    const created = await NullReportingReviewsAPI.createDraft({
      supplierOrgNr,
      period,
      reviewType: 1,
      receiverMail: null,
      emailTemplateId: null,
      emailSubject: null,
      emailBody: null,
    });

    await NullReportingReviewsAPI.addPhoneAttempt(created.id, {
      contactName: phoneContactName.trim(),
      attemptAt: phoneAttemptAt,
      notes: phoneNotes?.trim() ? phoneNotes.trim() : null,
       outcome: phoneOutcome === "" ? null : Number(phoneOutcome),
    });

    navigate(`/nullreporting/reviews/${created.id}`);
  };
const onTemplateChange = (raw: string) => {
  const tid = raw ? Number(raw) : null;
  setEmailTemplateId(tid);

  if (!tid) {
    setSubject("");
    setBody("");
    return;
  }

  const t = templates.find(x => x.id === tid);
  if (!t) return;

  setSubject(t.header ?? "");
  setBody(decodeHtmlEntities(t.body ?? ""));
};

  const addAttempt = async () => {
    if (!details) return;
    if (details.review.reviewType !== 1) return;

    if (!newAttemptContactName.trim()) throw new Error("Kontaktperson saknas");
    if (!newAttemptAt) throw new Error("Datum/tid saknas");

    await NullReportingReviewsAPI.addPhoneAttempt(details.review.id, {
      contactName: newAttemptContactName.trim(),
      attemptAt: newAttemptAt,
      notes: newAttemptNotes?.trim() ? newAttemptNotes.trim() : null,
      outcome: newAttemptOutcome === "" ? null : Number(newAttemptOutcome),
    });

    setNewAttemptContactName("");
    setNewAttemptNotes("");
    setNewAttemptOutcome("");
    await load();
  };

  const saveReply = async () => {
    if (!details) return;
    await NullReportingReviewsAPI.addReply(details.review.id, { text: supplierReply });
    await load();
  };

  const handleReview = async () => {
    if (!details) return;
    await NullReportingReviewsAPI.handle(details.review.id, { comment: handledComment });
    await load();
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  // guard
  if (isNew && (!supplierOrgNr || !period)) {
    return <div style={{ padding: 16 }}>Saknar supplierOrgNr/period i querystring.</div>;
  }

  return (
    <div className="nr-review-page">
      <div className="nr-review-top">
        <Stepper status={status} reviewType={reviewType} />
      </div>

      <div className="nr-review-card">
        {/* NEW */}
        {isNew ? (
          reviewType === "email" ? (
            // ---------------- EMAIL NEW ----------------
            <div className="nr-review-new">
              <div className="nr-review-grid">
                <div>
                  <div className="nr-review-label">Mottagare</div>

                  {contactsLoading ? (
                    <div style={{ padding: "6px 0" }}>
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : contacts.length > 0 ? (
                    <>
                      <select className="form-select" value={selectedContactId} onChange={(e) => onSelectContact(e.target.value)}>
                        <option value="">Välj kontakt…</option>
                        {contacts.map((c) => (
                          <option key={c.id} value={c.id}>
                            {(c.displayName && c.displayName.trim().length > 0 ? c.displayName : c.mail) + ` (${c.mail})`}
                          </option>
                        ))}
                      </select>

                      <div style={{ marginTop: 8 }}>
                        <input className="form-control" value={receiverMail} onChange={(e) => setReceiverMail(e.target.value)} />
                      </div>
                    </>
                  ) : (
                    <input
                      className="form-control"
                      value={receiverMail}
                      onChange={(e) => setReceiverMail(e.target.value)}
                      placeholder="kontakt@leverantor.se"
                    />
                  )}
                </div>

                <div>
                  <div className="nr-review-label">E-postmall</div>
                  {templatesLoading ? (
                    <div style={{ padding: "6px 0" }}>
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : (
                    <Form.Select
                      value={emailTemplateId ?? ""}
                      onChange={(e) => onTemplateChange(e.target.value) /*setEmailTemplateId(e.target.value ? Number(e.target.value) : null)*/}
                    >
                      <option value="">Välj mall…</option>
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.templateName}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <div className="nr-review-label">Ämne</div>
                <input className="form-control" value={subject} onChange={(e) => setSubject(e.target.value)} />
              </div>

              {/* <div style={{ marginTop: 12 }}>
                <div className="nr-review-label">Meddelande</div>
                <textarea
                  className="form-control nr-review-textarea"
                  rows={10}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
              </div> */}
              <div style={{ marginTop: 12 }}>
  <div className="nr-review-label">Meddelande</div>

  <RTE
   key={emailTemplateId ?? "no-template"}
    value={body ?? ''}
    onChange={(html) => setBody(html)}
    disabled={false}
  />
</div>

              <div className="nr-review-actions">
                <Button className="btn button-cancel" onClick={sendTestToMe} disabled={!subject && !body}>
                  Skicka test till mig
                </Button>

                <Button className="btn button-save" onClick={createAndSendEmail} disabled={!receiverMail || !subject || !body}>
                  <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 8 }} />
                  Skicka & skapa granskning
                </Button>
              </div>
            </div>
          ) : (
            // ---------------- PHONE NEW ----------------
            <div className="nr-review-new">
              <div className="nr-review-grid">
                <div>
                  <div className="nr-review-label">Kontaktad person</div>
                  <input
                    className="form-control"
                    value={phoneContactName}
                    onChange={(e) => setPhoneContactName(e.target.value)}
                    placeholder="Namn / roll"
                  />
                </div>

                <div>
                  <div className="nr-review-label">Datum/tid</div>
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={phoneAttemptAt}
                    onChange={(e) => setPhoneAttemptAt(e.target.value)}
                  />
                </div>
              </div>
            <div style={{ marginTop: 12 }}>
  <div className="nr-review-label">Utfall</div>
  <select
    className="form-select"
    value={phoneOutcome}
    onChange={(e) => setPhoneOutcome(e.target.value ? Number(e.target.value) : "")}
  >
    <option value="">Välj…</option>
    <option value="1">Inget svar</option>
    <option value="2">Upptaget</option>
    <option value="3">Svar erhållet</option>
  </select>
</div>

              <div style={{ marginTop: 12 }}>
                <div className="nr-review-label">Anteckningar</div>
                <textarea
                  className="form-control nr-review-textarea"
                  rows={8}
                  value={phoneNotes}
                  onChange={(e) => setPhoneNotes(e.target.value)}
                  placeholder="Skriv vad som sas / vad ni kom överens om / nästa steg..."
                />
              </div>

              <div className="nr-review-actions">
                <Button className="btn button-save" onClick={createPhoneReview} disabled={!phoneContactName.trim() || !phoneAttemptAt}>
                  Skapa telefongranskning
                </Button>
              </div>
            </div>
          )
        ) : (
          // ---------------- FOLLOWUP ----------------
          <div className="nr-review-followup">
            {/* EMAIL: show sent email */}
            {reviewType === "email" && (
              <div className="nr-review-sentbox">
                <div className="nr-review-sentbox-title">Vårt utskick</div>
                {/* <div
                  className="nr-review-sentbox-content"
                  dangerouslySetInnerHTML={{ __html: details?.review?.emailBody ?? "" }}
                /> */}
                <div className="nr-review-sentbox-content">
  <RTE  key={emailTemplateId ?? "no-template"} value={details?.review?.emailBody ?? ""} disabled={true} />
</div>
              </div>
            )}

            {/* PHONE: show attempts + add attempt */}
            {reviewType === "phone" && (
              <div className="nr-review-section">
                <div className="nr-review-section-head">
                  <h1 className="nr-review-section-title">Samtalsförsök</h1>
                  <span className="nr-review-badge">{details?.phoneAttempts?.length ?? 0} st</span>
                </div>

                <div style={{ marginBottom: 12 }}>
                  {(details?.phoneAttempts ?? []).map((a) => (
                    <div key={a.id} style={{ padding: "10px 0", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
  <b>{a.contactName}</b>
  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
    {getOutcomeBadge(a.outcome)}
    <span style={{ opacity: 0.75 }}>{String(a.attemptAt).replace("T", " ").slice(0, 16)}</span>
  </div>
</div>

                      {a.notes && <div style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>{a.notes}</div>}
                    </div>
                  ))}
                  {(details?.phoneAttempts?.length ?? 0) === 0 && <div style={{ opacity: 0.8 }}>Inga försök sparade ännu.</div>}
                </div>

                <div className="nr-review-grid">
                  <div>
                    <div className="nr-review-label">Ny kontakt</div>
                    <input
                      className="form-control"
                      value={newAttemptContactName}
                      onChange={(e) => setNewAttemptContactName(e.target.value)}
                      placeholder="Namn / roll"
                    />
                  </div>
                  <div>
                    <div className="nr-review-label">Datum/tid</div>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={newAttemptAt}
                      onChange={(e) => setNewAttemptAt(e.target.value)}
                    />
                  </div>
                </div>
                <div style={{ marginTop: 12 }}>
  <div className="nr-review-label">Utfall</div>
  <select
    className="form-select"
    value={newAttemptOutcome}
    onChange={(e) => setNewAttemptOutcome(e.target.value ? Number(e.target.value) : "")}
  >
    <option value="">Välj…</option>
    <option value="1">Inget svar</option>
    <option value="2">Upptaget</option>
    <option value="3">Svar erhållet</option>
  </select>
</div>


                <div style={{ marginTop: 12 }}>
                  <div className="nr-review-label">Anteckningar</div>
                  <textarea
                    className="form-control nr-review-textarea"
                    rows={4}
                    value={newAttemptNotes}
                    onChange={(e) => setNewAttemptNotes(e.target.value)}
                  />
                </div>

                <div className="nr-review-right" style={{ marginTop: 8 }}>
                  <Button className="btn button-cancel" onClick={addAttempt} disabled={!newAttemptContactName.trim() || !newAttemptAt}>
                    Spara samtalsförsök
                  </Button>
                </div>
              </div>
            )}

            {/* Supplier reply */}
            <div className="nr-review-section">
              <div className="nr-review-section-head">
                <h1 className="nr-review-section-title">Leverantörens svar</h1>
                {status >= 2 && <span className="nr-review-badge">Mottaget</span>}
              </div>

              <textarea className="form-control nr-review-textarea" rows={5} value={supplierReply} onChange={(e) => setSupplierReply(e.target.value)} />
              <div className="nr-review-right">
                <Button className="btn button-cancel" onClick={saveReply}>
                  Spara svar
                </Button>
              </div>
            </div>

            {/* Handle */}
            <div className="nr-review-closebox">
              <h1 className="nr-review-closebox-title">Slutmarkera granskning</h1>
              <div className="nr-review-closebox-hint">* Kommentar krävs för att avsluta ärendet</div>
              <textarea
                className="form-control nr-review-textarea"
                rows={3}
                value={handledComment}
                onChange={(e) => setHandledComment(e.target.value)}
                placeholder="Ange slutsats eller intern kommentar här..."
              />
              <Button className="btn button-save nr-review-closebtn" onClick={handleReview} disabled={!handledComment.trim()}>
                Slutmarkera & Arkivera
              </Button>
            </div>

            {/* Logs */}
            <div className="nr-review-logs">
              <h1 className="nr-review-logs-title">Systemlogg</h1>
              <div className="nr-review-logs-list">
                {details?.logs?.map((l) => (
                  <div key={l.id} className="nr-review-logrow">
                    <div className="nr-review-logleft">
                      <span className="nr-review-logtime">{String(l.timestamp).replace("T", " ").slice(0, 19)}</span>
                      <span className="nr-review-logmsg">{l.message}</span>
                    </div>
                    <span className="nr-review-logdot" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NullReportingReviewPage;
